import React from "react";
import { useLocation, useHistory } from "react-router-dom";
import api from "../api/http";
import { fetchPlans } from "../api/plans";
import { API_ENDPOINTS } from "../api/endpoints";
import { CREDIT_UPDATED_EVENT, emitCreditUpdated } from "../utils/creditEvent";
import { matchesInvoiceSelection, isUsableUnpaidInvoice, invoiceDisplayStatus, chooseInvoiceForBilling, invoiceStatusLabel } from "../utils/invoiceSelection";
import { useLanguage } from "../context/LanguageContext";
import BillingProfileModal from "../components/billing/BillingProfileModal";
import "./InvoiceSettings.css";
// Labels are fixed here; prices and credits are fallbacks only. The page uses
// the live values (and any scheduled change) from GET /api/v1/plans.
const DIRECT_PAYMENT_PLANS = [
  {
    planId: "plan_multi_s",
    label: "\u30d7\u30e9\u30f3S",
    tagline: "\u5c0f\u898f\u6a21\u30c1\u30fc\u30e0\u5411\u3051",
    labelEn: "Plan S", taglineEn: "For small teams",
    annual: 5000,
    monthly: 6000,
    credits: 250,
    creditLimit: 500,
  },
  {
    planId: "plan_multi_m",
    label: "\u30d7\u30e9\u30f3M",
    tagline: "\u6210\u9577\u4e2d\u306e\u30c1\u30fc\u30e0\u5411\u3051",
    labelEn: "Plan M", taglineEn: "For growing teams",
    annual: 21000,
    monthly: 24000,
    credits: 1200,
    creditLimit: 2400,
    recommended: true,
  },
  {
    planId: "plan_multi_l",
    label: "\u30d7\u30e9\u30f3L",
    tagline: "\u5927\u898f\u6a21\u5229\u7528\u5411\u3051",
    labelEn: "Plan L", taglineEn: "For large-scale use",
    annual: 42000,
    monthly: 50000,
    credits: 3000,
    creditLimit: 6000,
  },
];

function formatPlanChangeDate(value, english) {
  return new Date(value).toLocaleDateString(english ? "en-US" : "ja-JP", { timeZone: "Asia/Tokyo" });
}

const NOTIFIED_PAYMENT_INVOICE_KEY = "pithias.notifiedPaymentInvoiceId";
const ACTIVE_PAYMENT_INVOICE_KEY = "pithias.activePaymentInvoiceId";

// Kept in localStorage (not per tab) so a customer who pays in the bank app
// and comes back later, even in a new tab, still gets the confirmation once.
const getPaymentSessionValue = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setPaymentSessionValue = (key, value) => {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Payment tracking still works while this page stays open.
  }
};

// A bank transfer usually takes longer than a few minutes, so keep watching
// an open invoice: every 30 s for the first 30 min, then every 2 min, for at
// most 12 h. Hidden tabs skip checks and check immediately when shown again.
const PAYMENT_POLL_FAST_MS = 30 * 1000;
const PAYMENT_POLL_SLOW_MS = 2 * 60 * 1000;
const PAYMENT_POLL_FAST_FOR_MS = 30 * 60 * 1000;
const PAYMENT_POLL_MAX_MS = 12 * 60 * 60 * 1000;

export default function InvoiceSettings() {
  const { text, isEnglish } = useLanguage();
  const { search } = useLocation();
  const routerHistory = useHistory();
  const params = new URLSearchParams(search);

  const [agreed, setAgreed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [apiError, setApiError] = React.useState(null);
  const [successMessage, setSuccessMessage] = React.useState(null);
  const [receiptLoading, setReceiptLoading] = React.useState(null);
  const [receiptError, setReceiptError] = React.useState(null);
  const receiptBusyRef = React.useRef(false);

  const downloadReceipt = async (row) => {
    const id = row.id || row.invoiceId;
    if (receiptBusyRef.current) return;
    receiptBusyRef.current = true;
    setReceiptLoading(id);
    setReceiptError(null);
    try {
      const response = await api.get(`/api/v1/billing/invoices/${encodeURIComponent(id)}/receipt`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      const downloadDate = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tokyo' });
      const disposition = response.headers['content-disposition'] || '';
      const filename = /filename="(PITHIAS_Receipt_[A-Za-z0-9_-]+\.pdf)"/.exec(disposition);
      link.download = filename ? filename[1] : `PITHIAS_Receipt_${downloadDate}_${String(id).slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setReceiptError(text('領収書をダウンロードできませんでした。入金状況をご確認のうえ、再度お試しください。', 'Unable to download the receipt. Check the payment status and try again.'));
    } finally {
      receiptBusyRef.current = false;
      setReceiptLoading(null);
    }
  };

  const [invoice, setInvoice] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [license, setLicense] = React.useState(null);
  const [licenseLoaded, setLicenseLoaded] = React.useState(false);
  const [licenseLoadError, setLicenseLoadError] = React.useState(false);
  const [upgradeQuote, setUpgradeQuote] = React.useState(null);
  const [upgradeQuoteLoading, setUpgradeQuoteLoading] = React.useState(false);
  const [upgradeQuoteError, setUpgradeQuoteError] = React.useState("");
  const [bank, setBank] = React.useState(null);
  const [instruction, setInstruction] = React.useState(null);
  const [plan, setPlan] = React.useState(null);

  const [copyMessage, setCopyMessage] = React.useState("");
  const [copiedKey, setCopiedKey] = React.useState("");
  const [paymentSuccessOpen, setPaymentSuccessOpen] = React.useState(false);

  const paidShownRef = React.useRef(false);

  const planId = params.get("planId") || "";
  const selectedInvoiceId = params.get("invoiceId") || "";
  const cycleFromQuery = ["monthly", "annual"].includes(params.get("cycle")) ? params.get("cycle") : "";
  const boxAddonSelected = params.get("boxAddon") === "true";
  const boxAddonOnly = params.get("purchase") === "box";
  const upgradeRequested = params.get("purchase") === "upgrade";

  const [selectedCycle, setSelectedCycle] = React.useState(cycleFromQuery);
  // Billing name/address must be entered before the first invoice is issued.
  const [billingProfile, setBillingProfile] = React.useState({ complete: null, name: "" });
  const billingProfileCompleteRef = React.useRef(null);
  const [profileModal, setProfileModal] = React.useState(null); // { action: "web" | "email" | null }
  const profileAutoOpenedRef = React.useRef(false);
  const needsBillingProfile = () => billingProfileCompleteRef.current === false;
  const isBillingProfileRequiredError = (e) => e?.response?.data?.code === "BILLING_PROFILE_REQUIRED";
  // Server errors with a Japanese `error` and an English `message` (e.g. bank maintenance).
  const apiErrorText = (e) => {
    const data = e?.response?.data;
    if (["BANK_UNAVAILABLE", "UPGRADE_INVOICE_OPEN"].includes(data?.code)) return text(data.error, data.message);
    return data?.error || data?.message || e.message;
  };
  const [apiPlans, setApiPlans] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchPlans()
      .then((res) => {
        const rows = res?.data?.data ?? res?.data;
        if (!cancelled && Array.isArray(rows)) setApiPlans(rows);
      })
      .catch((error) => console.error("Failed to load plans", error));
    return () => { cancelled = true; };
  }, []);

  // Live prices/credits from the API merged onto the fixed labels.
  const PLANS = React.useMemo(() => DIRECT_PAYMENT_PLANS.map((item) => {
    const live = apiPlans?.find((row) => row.planId === item.planId);
    if (!live) return item;
    return {
      ...item,
      annual: Number(live.monthlyPriceAnnual),
      monthly: Number(live.monthlyPriceMonthly),
      credits: Number(live.grantedCredits ?? item.credits),
      creditLimit: Number(live.creditLimit ?? item.creditLimit),
      upcomingChange: live.upcomingChange || null,
    };
  }), [apiPlans]);

  React.useEffect(() => {
    setSelectedCycle(cycleFromQuery);
  }, [cycleFromQuery]);
  const refreshLicense = React.useCallback(async () => {
    try {
      const res = await api.get(API_ENDPOINTS.ME);
      setLicense(res.data?.data?.license || null);
      const me = res.data?.data?.user;
      if (me && typeof me.billingProfileComplete === "boolean") {
        billingProfileCompleteRef.current = me.billingProfileComplete;
        setBillingProfile({
          complete: me.billingProfileComplete,
          name: me.billingProfileComplete ? me.companyName : "",
        });
      }
      setLicenseLoadError(false);
    } catch {
      setLicenseLoadError(true);
    } finally {
      setLicenseLoaded(true);
    }
  }, []);

  React.useEffect(() => {
    refreshLicense();
    window.addEventListener(CREDIT_UPDATED_EVENT, refreshLicense);
    return () => window.removeEventListener(CREDIT_UPDATED_EVENT, refreshLicense);
  }, [refreshLicense]);

  const isActiveSubscription = license?.status === "active";
  const currentPlan = PLANS.find((item) => item.planId === license?.planId);
  const targetPlan = PLANS.find((item) => item.planId === planId);
  // The plan the customer renews into (a scheduled downgrade/cycle switch wins).
  const renewalPlan = PLANS.find((item) => item.planId === (license?.pendingPlanId || license?.planId));
  const renewalPriceNotice =
    license?.status === "active" && renewalPlan?.upcomingChange && license?.subscriptionEndDate
      ? {
          label: renewalPlan.label,
          labelEn: renewalPlan.labelEn,
          change: renewalPlan.upcomingChange,
          cycle: license.pendingBillingCycle || license.billingCycle,
          renewalAt: license.subscriptionEndDate,
          // New prices apply to the first term starting on/after the effective date.
          appliesAtNextRenewal:
            new Date(renewalPlan.upcomingChange.effectiveFrom).getTime() <= new Date(license.subscriptionEndDate).getTime(),
        }
      : null;
  const inferredUpgrade = Boolean(
    !selectedInvoiceId && isActiveSubscription && currentPlan && targetPlan &&
    // Same rule as the backend: a plan with a higher credit limit is an upgrade.
    Number(targetPlan.creditLimit) > Number(currentPlan.creditLimit)
  );
  // Infer upgrade intent from the authenticated account as well as the URL.
  // This protects users arriving from an older/stale pricing page that omitted
  // `purchase=upgrade`; the backend independently enforces the same rule.
  const upgradePurchase = !boxAddonOnly && (upgradeRequested || inferredUpgrade);
  const activeUpgradeFlow = isActiveSubscription && !boxAddonOnly && !selectedInvoiceId;
  // Active subscriber picking a lower plan or another billing cycle: the change
  // is scheduled for the next term and no invoice is issued now.
  const nextTermChange = isActiveSubscription && !upgradePurchase && !boxAddonOnly &&
    Boolean(planId) && !invoice && !selectedInvoiceId;
  const hasPlanSelection = Boolean(planId && selectedCycle);
  const hasPurchaseSelection = hasPlanSelection || boxAddonOnly;
  const readOnlyInvoice = Boolean(invoice && !isUsableUnpaidInvoice(invoice));
  const termsEnabled = !readOnlyInvoice && Boolean(hasPurchaseSelection || invoice);
  const disabled = !agreed || loading || readOnlyInvoice || (!hasPurchaseSelection && !invoice) ||
    ((!licenseLoaded || licenseLoadError) && !invoice) ||
    (upgradePurchase && (!upgradeQuote || upgradeQuoteLoading || Boolean(upgradeQuoteError)));

  React.useEffect(() => {
    if (inferredUpgrade && !cycleFromQuery && ["monthly", "annual"].includes(license?.billingCycle)) {
      setSelectedCycle(license.billingCycle);
    }
  }, [inferredUpgrade, cycleFromQuery, license?.billingCycle]);

  React.useEffect(() => {
    if (!upgradePurchase || !planId || !selectedCycle || selectedInvoiceId) {
      setUpgradeQuote(null);
      setUpgradeQuoteError("");
      return undefined;
    }
    let current = true;
    setUpgradeQuote(null);
    setUpgradeQuoteError("");
    setUpgradeQuoteLoading(true);
    const query = new URLSearchParams({ planId, billingCycle: selectedCycle });
    api.get(`${API_ENDPOINTS.BILLING_QUOTE}?${query.toString()}`).then((res) => {
      if (current) setUpgradeQuote(res.data?.data || null);
    }).catch((error) => {
      if (current) setUpgradeQuoteError(
        error?.response?.data?.message || error?.response?.data?.error ||
        text('アップグレード見積もりを取得できませんでした。', 'Unable to calculate the upgrade quote.')
      );
    }).finally(() => {
      if (current) setUpgradeQuoteLoading(false);
    });
    return () => { current = false; };
  }, [upgradePurchase, planId, selectedCycle, selectedInvoiceId, text]);

  React.useEffect(() => {
    if (!termsEnabled) setAgreed(false);
  }, [termsEnabled]);

  const normalizeItems = React.useCallback((data) => {
    return Array.isArray(data) ? data : data?.items || [];
  }, []);


  const copyText = React.useCallback(async (value, label) => {
    try {
      if (!value || value === "-") return;
      await navigator.clipboard.writeText(String(value));
      setCopiedKey(label);
      setCopyMessage(`${label}をコピーしました`);
      setTimeout(() => {
        setCopiedKey("");
        setCopyMessage("");
      }, 1200);
    } catch (e) {
      console.error("copy failed", e);
      setCopyMessage("コピーに失敗しました");
      setTimeout(() => setCopyMessage(""), 1800);
    }
  }, []);

  const buildBankFromInvoice = React.useCallback((inv) => {
    if (!inv) return null;

    return {
      name: "GMOあおぞらネット銀行",
      branchCode: inv.vaBranchCode || "-",
      branchName: inv.vaBranchNameKana || "-",
      type: "普通",
      number: inv.vaAccountNumber || "-",
      holder: inv.vaHolderNameKana || "-",
      vaId: inv.vaId || "-",
    };
  }, []);

  const buildInstructionFromInvoice = React.useCallback((inv) => {
    if (!inv?.amount) return null;
    return `Transfer exactly ¥${inv.amount} (JPY) to the assigned virtual account below.`;
  }, []);

  const mergePlanState = React.useCallback(
    (prevPlan, nextData = {}) => {
      return {
        ...(prevPlan || {}),
        ...(nextData || {}),
        planId:
          nextData?.planId ??
          prevPlan?.planId ??
          planId ??
          "",
        cycle:
          nextData?.cycle ??
          prevPlan?.cycle ??
          selectedCycle ??
          "",
      };
    },
    [planId, selectedCycle]
  );

  const loadHistory = React.useCallback(async () => {
    const res = await api.get(API_ENDPOINTS.BILLING_INVOICES);
    const items = normalizeItems(res.data);
    setHistory(items);
    return items;
  }, [normalizeItems]);

  const refreshCurrentInvoice = React.useCallback(
    async (invoiceId, fallbackInvoice = null) => {
      const items = await loadHistory();
      if (!invoiceId) return null;

      const latest = items.find((x) => x.id === invoiceId);
      if (!latest) return null;

      setInvoice((prev) => ({
        ...(prev || fallbackInvoice || {}),
        ...latest,
      }));
      setBank(buildBankFromInvoice(latest));
      setInstruction(buildInstructionFromInvoice(latest));
      setPlan((prev) =>
        mergePlanState(prev, {
          planId: latest.planId || planId || "",
          cycle: latest.cycle || prev?.cycle || selectedCycle || "",
        })
      );

      return latest;
    },
    [
      loadHistory,
      buildBankFromInvoice,
      buildInstructionFromInvoice,
      mergePlanState,
      planId,
      selectedCycle,
    ]
  );

  const restoreLatestUnpaidInvoice = React.useCallback(
    (items) => {
      if (!items?.length) return null;

      const matched = chooseInvoiceForBilling(items,
        {planId, cycle: selectedCycle, boxOnly: boxAddonOnly, boxAddon: boxAddonSelected}, selectedInvoiceId);

      if (!matched) return null;

      setInvoice(matched);
      // A newly created invoice is also opened with an invoiceId query string.
      // Do not treat that as an already-seen payment. A paid invoice that is
      // explicitly selected should still notify once unless this tab already
      // showed its confirmation (covers returning after a bank transfer).
      const wasAlreadyNotified =
        getPaymentSessionValue(NOTIFIED_PAYMENT_INVOICE_KEY) === matched.id;
      paidShownRef.current = matched.status === "PAID" &&
        wasAlreadyNotified &&
        getPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY) !== matched.id;
      setBank(buildBankFromInvoice(matched));
      setInstruction(buildInstructionFromInvoice(matched));
      setPlan((prev) =>
        mergePlanState(prev, {
          planId: matched.planId || planId || "",
          cycle: matched.cycle || prev?.cycle || selectedCycle || "",
        })
      );

      return matched;
    },
    [
      boxAddonOnly,
      boxAddonSelected,
      selectedInvoiceId,
      planId,
      selectedCycle,
      buildBankFromInvoice,
      buildInstructionFromInvoice,
      mergePlanState,
    ]
  );

  const createInvoice = React.useCallback(async () => {
    if (!boxAddonOnly && !["monthly", "annual"].includes(selectedCycle)) {
      throw new Error(text('月次または年次の請求期間を選択してください。', 'Please select monthly or annual billing.'));
    }
    const res = await api.post(API_ENDPOINTS.BILLING_INVOICES, {
      termsAccepted: agreed,
      ...(boxAddonOnly
        ? { purchaseType: "box_addon" }
        : {
            ...(upgradePurchase ? { purchaseType: "upgrade" } : {}),
            planId,
            cycle: selectedCycle,
            boxAddon: boxAddonSelected,
          }),
    });

    const data = res.data;
    if (data?.scheduled || data?.canceled || data?.unchanged) {
      setInvoice(null);
      setBank(null);
      setInstruction(null);
      setSuccessMessage(data.canceled
        ? text('次期プラン変更を取り消しました。', 'The scheduled next-term change was canceled.')
        : data.unchanged
          ? text('現在のプランと請求サイクルは変更されていません。', 'This is already your current plan and billing cycle.')
          : data.renewalInvoiceSyncDeferred
            ? text(
              '次期プランの変更を保存し、請求書を更新しました。メール送信は再試行されます。',
              'Your next-term plan change is saved and the invoice is updated. Email delivery will be retried.'
            )
            : text(
              `${data.pendingPlan?.planName ?? ''}への変更は次回契約開始時に適用されます。今回は請求されません。`,
              `Your change to ${data.pendingPlan?.planName ?? 'the selected plan'} is scheduled for the next term. No payment is due now.`
            ));
      // Refresh the "next-term plan" banner right away.
      await Promise.all([loadHistory(), refreshLicense()]);
      return data;
    }
    // A new upgrade request replaces the earlier unpaid upgrade invoice
    // (agreed 2 Nov 2026); tell the customer not to pay the old one.
    setSuccessMessage(Array.isArray(data?.replacedInvoiceIds) && data.replacedInvoiceIds.length > 0
      ? text(
        '以前のアップグレード請求書は無効になりました。お振込みは下記の新しい請求書の口座へお願いいたします。',
        'Your earlier upgrade invoice is no longer valid. Please pay the new invoice below.'
      )
      : null);
    setInvoice(data?.invoice ?? null);
    setBank(data?.bank ?? null);
    setInstruction(data?.instruction ?? null);
    setPlan((prev) => mergePlanState(prev, data?.plan ?? null));
    if (data?.invoice?.id) {
      setPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY, data.invoice.id);
      // Persist the invoice in the URL only after the user explicitly creates
      // or requests it (the action is gated by the terms checkbox).
      const nextParams = new URLSearchParams(search);
      nextParams.set("invoiceId", data.invoice.id);
      if (planId) nextParams.set("planId", planId);
      if (selectedCycle) nextParams.set("cycle", selectedCycle);
      if (upgradePurchase) nextParams.set("purchase", "upgrade");
      else if (boxAddonOnly) nextParams.set("purchase", "box");
      if (boxAddonSelected) nextParams.set("boxAddon", "true");
      routerHistory.replace(`/settings/invoice?${nextParams.toString()}`);
    }

    await loadHistory();
    return data;
  }, [planId, selectedCycle, boxAddonSelected, boxAddonOnly, upgradePurchase, loadHistory, refreshLicense, mergePlanState, routerHistory, search, text, agreed]);

  const handleWebInvoice = async () => {
    if (invoice && !isUsableUnpaidInvoice(invoice)) return;
    setApiError(null);
    setSuccessMessage(null);
    if (needsBillingProfile()) {
      setProfileModal({ action: "web" });
      return;
    }
    if (invoice?.id) {
      const existing = document.querySelector('.invoice-settings-generated-card') || document.querySelector('.invoice-history-scroll');
      existing?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setLoading(true);
    try {
      await createInvoice();
      paidShownRef.current = false;
    } catch (e) {
      console.error("Create invoice failed", e);
      if (isBillingProfileRequiredError(e)) {
        billingProfileCompleteRef.current = false;
        setBillingProfile({ complete: false, name: "" });
        setProfileModal({ action: "web" });
        return;
      }
      setApiError(
        apiErrorText(e)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRequest = async () => {
    if (invoice && !isUsableUnpaidInvoice(invoice)) return;
    setApiError(null);
    if (needsBillingProfile()) {
      setProfileModal({ action: "email" });
      return;
    }
    setLoading(true);

    try {
      let currentInvoice = invoice;

      if (!isUsableUnpaidInvoice(currentInvoice) ||
          !matchesInvoiceSelection(currentInvoice, {planId, cycle: selectedCycle, boxOnly: boxAddonOnly, boxAddon: boxAddonSelected})) {
        const created = await createInvoice();
        currentInvoice = created.invoice;
        paidShownRef.current = false;
      }

      await api.post(
        `${API_ENDPOINTS.BILLING_INVOICES}/${currentInvoice.id}/send-email`
      );

      alert("請求メールを送信しました。");
      await refreshCurrentInvoice(currentInvoice.id, currentInvoice);
    } catch (e) {
      console.error("Send invoice email failed", e);
      if (isBillingProfileRequiredError(e)) {
        billingProfileCompleteRef.current = false;
        setBillingProfile({ complete: false, name: "" });
        setProfileModal({ action: "email" });
        return;
      }
      setApiError(
        apiErrorText(e)
      );
    } finally {
      setLoading(false);
    }
  };

  // A trial customer choosing a plan is asked for the billing details first,
  // so the invoice isn't addressed to "Trial Company".
  React.useEffect(() => {
    if (profileAutoOpenedRef.current) return;
    if (hasPurchaseSelection && billingProfile.complete === false) {
      profileAutoOpenedRef.current = true;
      setProfileModal({ action: null });
    }
  }, [hasPurchaseSelection, billingProfile.complete]);

  const handleBillingProfileSaved = async (profile) => {
    billingProfileCompleteRef.current = true;
    setBillingProfile({ complete: true, name: profile?.billingName || profile?.companyName || profile?.personName || "" });
    const action = profileModal?.action;
    setProfileModal(null);
    // Unpaid invoices were re-addressed by the server; show the new name.
    if (invoice?.id) await refreshCurrentInvoice(invoice.id, invoice);
    if (action === "web") await handleWebInvoice();
    else if (action === "email") await handleEmailRequest();
  };

  React.useEffect(() => {
    let active = true;
    setInvoice(null);
    setBank(null);
    setInstruction(null);
    setPlan(null);
    (async () => {
      try {
        const items = await loadHistory();
        if (!active) return;
        // Explicit invoice IDs may open read-only history details. A plan visit
        // must not silently attach a prior unpaid invoice from history/session.
        if (selectedInvoiceId) {
          restoreLatestUnpaidInvoice(items);
          return;
        }
        // A selected plan is a quote/consent step, not an implicit invoice
        // selection. Older unpaid invoices remain available from history only.
        if (!hasPurchaseSelection) {
          // Back after paying (e.g. from the bank app): confirm the payment
          // the customer started here, once.
          const activeId = getPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY);
          const paidActive = activeId && items.find((x) => x.id === activeId && x.status === "PAID");
          if (paidActive) {
            paidShownRef.current = false;
            setInvoice(paidActive);
          } else if (activeId && !items.some((x) => x.id === activeId && x.status === "UNPAID")) {
            // Expired, canceled or another account's id: stop tracking it.
            setPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY, null);
          }
          return;
        }
      } catch (e) {
        console.error("Initial history load failed", e);
      }
    })();
    return () => { active = false; };
  }, [
    loadHistory,
    restoreLatestUnpaidInvoice,
    buildBankFromInvoice,
    buildInstructionFromInvoice,
    mergePlanState,
    planId,
    selectedCycle,
    boxAddonOnly,
    boxAddonSelected,
    selectedInvoiceId,
    hasPurchaseSelection,
  ]);

  const watchedPaymentInvoiceId = invoice?.id;
  const watchedPaymentStatus = invoice?.status;
  const watchedPaymentCreatedAt = invoice?.createdAt;

  React.useEffect(() => {
    if (!watchedPaymentInvoiceId || watchedPaymentStatus !== "UNPAID") {
      return undefined;
    }

    const watchedInvoiceId = watchedPaymentInvoiceId;
    const fallbackInvoice = {
      id: watchedPaymentInvoiceId,
      createdAt: watchedPaymentCreatedAt,
    };
    let cancelled = false;
    let pollTimer = null;
    let checking = false;
    let lastCheckAt = 0;
    const startedAt = Date.now();

    const scheduleNext = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= PAYMENT_POLL_MAX_MS) return;
      const delay = elapsed < PAYMENT_POLL_FAST_FOR_MS ? PAYMENT_POLL_FAST_MS : PAYMENT_POLL_SLOW_MS;
      if (pollTimer) window.clearTimeout(pollTimer);
      pollTimer = window.setTimeout(checkPaymentStatus, delay);
    };

    const checkPaymentStatus = async () => {
      if (cancelled || checking) return;
      if (document.hidden || receiptBusyRef.current) { scheduleNext(); return; }

      checking = true;
      lastCheckAt = Date.now();
      try {
        // Automatic checks read status only. The bank webhook confirms payments.
        const latest = await refreshCurrentInvoice(
          watchedInvoiceId,
          fallbackInvoice
        );

        if (latest?.status === "PAID") return;
      } catch (error) {
        console.error("Payment status refresh failed", error);
      } finally {
        checking = false;
      }

      scheduleNext();
    };

    // Customers usually switch to their bank app to pay; check as soon as
    // they come back to this tab.
    const onVisible = () => {
      if (document.hidden || cancelled) return;
      if (Date.now() - lastCheckAt < 5000) return;
      if (pollTimer) window.clearTimeout(pollTimer);
      checkPaymentStatus();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    scheduleNext();

    return () => {
      cancelled = true;
      if (pollTimer) window.clearTimeout(pollTimer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [
    watchedPaymentInvoiceId,
    watchedPaymentStatus,
    watchedPaymentCreatedAt,
    refreshCurrentInvoice,
  ]);

  React.useEffect(() => {
    if (!invoice?.id) return;
    if (invoice.status !== "PAID") return;
    if (paidShownRef.current) return;

    paidShownRef.current = true;

    const alreadyNotified =
      getPaymentSessionValue(NOTIFIED_PAYMENT_INVOICE_KEY) === invoice.id;
    const activePaymentFlow =
      getPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY) === invoice.id;
    setPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY, null);
    setTimeout(() => {
      emitCreditUpdated();
    }, 800);

    if (!alreadyNotified || activePaymentFlow) {
      setPaymentSessionValue(NOTIFIED_PAYMENT_INVOICE_KEY, invoice.id);
      setPaymentSuccessOpen(true);
    }
  }, [invoice?.id, invoice?.status]);


  const resetPaymentFlow = React.useCallback(() => {
    setPaymentSuccessOpen(false);
    if (getPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY) === invoice?.id) {
      setPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY, null);
    }
    setInvoice(null);
    setBank(null);
    setInstruction(null);
    setPlan(null);
    setAgreed(false);
    setSelectedCycle("");
    setApiError(null);
    paidShownRef.current = false;
    routerHistory.replace("/settings/invoice");
  }, [invoice?.id, routerHistory]);
  React.useEffect(() => {
    if (!paymentSuccessOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") resetPaymentFlow();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [paymentSuccessOpen, resetPaymentFlow]);

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("ja-JP");
    } catch {
      return value;
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString("ja-JP");
    } catch {
      return value;
    }
  };

  const amountText = invoice?.amount
    ? `¥${Number(invoice.amount).toLocaleString()}`
    : "-";

  const invoiceNumber = invoice?.id
    ? `INV-${invoice.id.slice(0, 8).toUpperCase()}`
    : "-";

  const registrationNumber = invoice?.issuerRegistrationNumber || "-";
  const customerCompanyName = invoice?.billingCompanyName || "株式会社サンプル";

  const effectiveCycle = plan?.cycle || selectedCycle || "";
  const isAnnual = effectiveCycle === "annual";

  const totalAmount = invoice?.amount != null ? Number(invoice.amount) : null;

  const annualBaseAmountForDisplay =
    isAnnual && invoice?.baseAmount != null
      ? Number(invoice.baseAmount)
      : isAnnual && totalAmount != null
        ? Math.round(totalAmount / 1.1)
        : null;

  const baseAmountForDisplay =
    invoice?.baseAmount != null
      ? Number(invoice.baseAmount)
      : totalAmount != null
        ? Math.round(totalAmount / 1.1)
        : null;

  const taxAmountForDisplay =
    Number.isFinite(totalAmount) && Number.isFinite(baseAmountForDisplay)
      ? totalAmount - baseAmountForDisplay
      : null;

  const boxAddonAmountForDisplay = invoice?.boxAddonIncluded
    ? Number(invoice.boxAddonAmount || 0)
    : 0;

  const planBaseAmountForDisplay =
    Number.isFinite(baseAmountForDisplay) && Number.isFinite(boxAddonAmountForDisplay)
      ? baseAmountForDisplay - boxAddonAmountForDisplay
      : null;

  const planMonthlyAmountForDisplay =
    isAnnual && Number.isFinite(planBaseAmountForDisplay)
      ? Math.round(planBaseAmountForDisplay / 12)
      : null;

  const annualBreakdownText =
    isAnnual &&
      Number.isFinite(planMonthlyAmountForDisplay) &&
      Number.isFinite(planBaseAmountForDisplay)
      ? invoice?.boxAddonIncluded
        ? `プラン ¥${planMonthlyAmountForDisplay.toLocaleString()} × 12 = ¥${planBaseAmountForDisplay.toLocaleString()}、BOX ¥${Number(invoice.boxAddonUnitPrice || 500).toLocaleString()} × ${invoice.boxAddonQuantity || 1} × 12 = ¥${boxAddonAmountForDisplay.toLocaleString()}`
        : `¥${planMonthlyAmountForDisplay.toLocaleString()} × 12 = ¥${planBaseAmountForDisplay.toLocaleString()}`
      : null;

  const cycleLabel =
    effectiveCycle === "annual"
      ? "年次"
      : effectiveCycle === "monthly"
        ? "月次"
        : effectiveCycle || "-";

  const settlementLabel = (row) => invoiceStatusLabel(row, text);

  const detailRow = (label, value, copyLabel) => {
    const key = copyLabel || label;
    const active = copiedKey === key;

    return (
      <div
        className="invoice-settings-detail-row"
        key={label}
        style={{
          display: "grid",
          gridTemplateColumns: "160px 120px 1fr 88px",
          alignItems: "center",
          gap: 12,
          padding: "10px 0",
          borderBottom: "1px solid #edf1f7",
        }}
      >
        <div style={{ color: "#49566b", fontWeight: 700, fontSize: 14 }}>
          {label}
        </div>

        <div
          style={{
            color: "#5d6b82",
            fontSize: 14,
            fontWeight: 700,
          }}
        />

        <div
          style={{
            color: "#1a2233",
            fontSize: 15,
            fontWeight: 700,
            letterSpacing:
              label.includes("番号") || label.includes("コード")
                ? "0.03em"
                : "normal",
            wordBreak: "break-word",
          }}
        >
          {value || "-"}
        </div>

        {/* {planId && !cycleFromQuery && (
          <div
            style={{
              marginTop: 16,
              padding: 18,
              border: "1px solid #dbe4ff",
              background: "#f8fbff",
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
              請求サイクルを選択してください
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setSelectedCycle("annual")}
                style={{
                  minWidth: 180,
                  height: 48,
                  borderRadius: 10,
                  border: selectedCycle === "annual" ? "2px solid #6163FF" : "1px solid #cbd5e1",
                  background: selectedCycle === "annual" ? "#eef2ff" : "#fff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                年次請求
              </button>

              <button
                type="button"
                onClick={() => setSelectedCycle("monthly")}
                style={{
                  minWidth: 180,
                  height: 48,
                  borderRadius: 10,
                  border: selectedCycle === "monthly" ? "2px solid #6163FF" : "1px solid #cbd5e1",
                  background: selectedCycle === "monthly" ? "#eef2ff" : "#fff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                月次請求
              </button>
            </div>
          </div>
        )} */}

        <div className="invoice-settings-copy-cell" style={{ textAlign: "right" }}>
          <button
            type="button"
            className="invoice-settings-copy-button"
            title="クリックしてコピー"
            onClick={() => copyText(value, key)}
            style={{
              width: 110,
              height: 36,
              borderRadius: 10,
              border: active ? "1px solid #16a34a" : "1px solid #2563eb",
              background: active
                ? "linear-gradient(135deg, #16a34a, #22c55e)"
                : "linear-gradient(135deg, #2563eb, #3b82f6)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: active ? "scale(1.05)" : "scale(1)",
              boxShadow: active
                ? "0 10px 20px rgba(22,163,74,0.35)"
                : "0 4px 10px rgba(37,99,235,0.25)",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onMouseEnter={(e) => {
              if (!active) {
                e.currentTarget.style.transform = "translateY(-2px) scale(1.03)";
                e.currentTarget.style.boxShadow =
                  "0 8px 18px rgba(37,99,235,0.35)";
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 4px 10px rgba(37,99,235,0.25)";
              }
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = "scale(0.92)";
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = active ? "scale(1.05)" : "scale(1)";
            }}
          >
            {active ? "コピー済み" : "コピー"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="invoice-settings-page" style={{ maxWidth: 980, margin: "40px auto", padding: "0 20px 48px" }}>
      {paymentSuccessOpen && (
        <div
          className="payment-success-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) resetPaymentFlow();
          }}
        >
          <section
            className="payment-success-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-success-title"
          >
            <div className="payment-success-icon" aria-hidden="true">{"\u2713"}</div>
            <div className="payment-success-eyebrow">{text('お支払い完了', 'Payment complete')}</div>
            <h2 id="payment-success-title">{text('お支払いを確認しました', 'Payment confirmed')}</h2>
            <p>
              {text('ご入金が正常に確認されました。クレジットはアカウントに反映されています。', 'Your payment was confirmed. The credits have been added to your account.')}
            </p>
            {Number(invoice?.grantedCredits) > 0 && (
              <div className="payment-success-credit">
                <span>{text('反映クレジット', 'Credits added')}</span>
                <strong>{"+" + Number(invoice.grantedCredits).toLocaleString()}</strong>
              </div>
            )}
            <button type="button" autoFocus onClick={resetPaymentFlow}>
              {text('確認', 'Confirm')}
            </button>
          </section>
        </div>
      )}
      <a
        href="/ocr-extract-view"
        className="invoice-settings-back-button"
        onClick={(event) => {
          event.preventDefault();
          window.location.assign("/ocr-extract-view");
        }}
      >
        <span aria-hidden="true">←</span>
        {text('抽出画面へ戻る', 'Back to extraction')}
      </a>

      <h2
        style={{
          fontSize: 52,
          marginBottom: 18,
          color: "#141d2f",
          letterSpacing: "0.02em",
        }}
      >
        {text('ご請求・お支払い', 'Billing and payment')}
      </h2>

      <p
        style={{
          marginTop: 0,
          fontSize: 18,
          color: "#4a5568",
          marginBottom: 24,
        }}
      >
        {text('ご希望のプランに基づき、請求書を発行いたします。', 'Create an invoice based on your selected plan.')}
      </p>

      {licenseLoaded && license && (
        <section
          className="credit-summary"
          aria-label={text('クレジット内訳', 'Credit balance breakdown')}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "stretch",
            marginBottom: 24,
          }}
        >
          <div style={{ flex: "1 1 200px", padding: "14px 18px", border: "1px solid #dbe4ff", borderRadius: 12, background: "#f8fbff" }}>
            <div style={{ color: "#64748b", fontSize: 13 }}>{text('プランクレジット（繰越を含む）', 'Plan credits (including carryover)')}</div>
            <strong style={{ color: "#172554", fontSize: 22 }}>{Number(license.planCreditsRemaining ?? Math.max((license.creditLimit || 0) - (license.creditsConsumed || 0), 0)).toLocaleString()}</strong>
          </div>
          <div style={{ flex: "1 1 200px", padding: "14px 18px", border: "1px solid #dbe4ff", borderRadius: 12, background: "#f8fbff" }}>
            <div style={{ color: "#64748b", fontSize: 13 }}>{text('購入クレジット', 'Purchased credits')}</div>
            <strong style={{ color: "#172554", fontSize: 22 }}>{Number(license.purchasedCreditsRemaining ?? 0).toLocaleString()}</strong>
            {Array.isArray(license.purchasedCreditLots) && license.purchasedCreditLots.length > 0 && (
              <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
                {license.purchasedCreditLots.map((lot, index) => (
                  <div key={`${lot.expiresAt || 'never'}-${index}`}>
                    {Number(lot.remaining).toLocaleString()} · {lot.expiresAt
                      ? text(`有効期限 ${new Date(lot.expiresAt).toLocaleDateString('ja-JP')}`, `Expires ${new Date(lot.expiresAt).toLocaleDateString('en-US')}`)
                      : text('有効期限なし', 'No expiration')}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ flex: "1 1 200px", padding: "14px 18px", border: "1px solid #dbe4ff", borderRadius: 12, background: "#f8fbff" }}>
            <div style={{ color: "#64748b", fontSize: 13 }}>{text('合計残高', 'Total available')}</div>
            <strong style={{ color: "#172554", fontSize: 22 }}>{Number(license.creditsRemaining || 0).toLocaleString()}</strong>
          </div>
        </section>
      )}

      {licenseLoaded && license?.pendingPlanId && license?.pendingChangeEffectiveAt && (
        <div
          role="status"
          className="next-term-banner"
          style={{ marginBottom: 20, padding: "14px 18px", border: "1px solid #bfd4ff", borderRadius: 12, background: "#f2f7ff", color: "#1e3a8a" }}
        >
          {text(
            `次期プラン: ${license.pendingPlanId.replace("plan_multi_", "").toUpperCase()}（${license.pendingBillingCycle === "annual" ? "年次" : "月次"}）。適用日: ${new Date(license.pendingChangeEffectiveAt).toLocaleDateString("ja-JP")}。現在の契約終了まで追加請求はありません。`,
            `Next-term plan: ${license.pendingPlanId.replace("plan_multi_", "").toUpperCase()} (${license.pendingBillingCycle === "annual" ? "annual" : "monthly"}). Effective ${new Date(license.pendingChangeEffectiveAt).toLocaleDateString("en-US")}. No additional charge applies before the current term ends.`
          )}
        </div>
      )}

      {licenseLoaded && renewalPriceNotice && (
        <div
          role="status"
          style={{ marginBottom: 20, padding: "14px 18px", border: "1px solid #fcd34d", borderRadius: 12, background: "#fffbeb", color: "#92400e" }}
        >
          {text(
            `料金改定のお知らせ: ${renewalPriceNotice.label}は${formatPlanChangeDate(renewalPriceNotice.change.effectiveFrom, false)}から${renewalPriceNotice.cycle === "annual" ? `年次請求 月額¥${Number(renewalPriceNotice.change.monthlyPriceAnnual).toLocaleString()}` : `月額¥${Number(renewalPriceNotice.change.monthlyPriceMonthly).toLocaleString()}`}（税抜）になります。${renewalPriceNotice.appliesAtNextRenewal ? `次回の契約更新（${formatPlanChangeDate(renewalPriceNotice.renewalAt, false)}）から適用され、` : `${formatPlanChangeDate(renewalPriceNotice.change.effectiveFrom, false)}以降に開始する契約更新から適用され、`}現在の契約期間の料金は変わりません。`,
            `Price update: ${renewalPriceNotice.labelEn} will be ${renewalPriceNotice.cycle === "annual" ? `¥${Number(renewalPriceNotice.change.monthlyPriceAnnual).toLocaleString()}/month billed annually` : `¥${Number(renewalPriceNotice.change.monthlyPriceMonthly).toLocaleString()}/month`} (before tax) from ${formatPlanChangeDate(renewalPriceNotice.change.effectiveFrom, true)}. ${renewalPriceNotice.appliesAtNextRenewal ? `It applies from your next renewal (${formatPlanChangeDate(renewalPriceNotice.renewalAt, true)}).` : "It applies from your first renewal on or after that date."} Your current term's price does not change.`
          )}
        </div>
      )}

      {boxAddonOnly && !invoice && (
        <section className="payment-plan-selector" aria-labelledby="box-addon-payment-heading">
          <div className="payment-step-heading">
            <span className="payment-step-number">1</span>
            <div>
              <h3 id="box-addon-payment-heading">{text('BOX連携オプションの追加', 'Add Box integration')}</h3>
              <p>{text('現在のプランは変更せず、BOX連携料金のみ請求します。', 'Keep the current plan and invoice only the Box integration fee.')}</p>
            </div>
          </div>
          <div style={{ padding: 20, border: "1px solid #dbe4ff", borderRadius: 12, background: "#f8fbff" }}>
            <strong style={{ fontSize: 22 }}>{text('BOX連携オプション', 'Box integration add-on')}</strong>
            <div style={{ marginTop: 8, color: "#4a5568" }}>{text('¥500 / 月（ユーザーあたり・税別）', '¥500 / month per user, before tax')}</div>
          </div>
        </section>
      )}

      {!planId && !boxAddonOnly && !invoice && (
        <section className="payment-plan-selector" aria-labelledby="payment-plan-heading">
          <div className="payment-step-heading">
            <span className="payment-step-number">1</span>
            <div>
              <h3 id="payment-plan-heading">
                {text('ご希望のプランを選択', 'Select your preferred plan')}
              </h3>
              <p>
                {!licenseLoaded
                  ? text('ご契約情報を確認しています…', 'Checking your subscription…')
                  : activeUpgradeFlow
                  ? text('上位プランへは今すぐ変更できます（差額のみ日割りで請求、更新日は変わりません）。同じ・下位プランや請求サイクルの変更は次回更新日から適用されます。', 'Upgrade now and pay only the prorated difference (your renewal date stays the same). A lower plan or a different billing cycle starts at your next renewal.')
                  : boxAddonSelected
                  ? text('プランとBOX連携オプションをまとめて1枚の請求書で発行します。', 'The plan and Box add-on will be combined into one invoice.')
                  : text('利用規模に合わせてプランをお選びください', 'Choose a plan that matches your usage.')}
              </p>
            </div>
          </div>

          <div className="payment-plan-grid">
            {PLANS.map((item) => (
              (() => {
                const itemCredits = Number(item.creditLimit);
                const currentCredits = Number(currentPlan?.creditLimit || 0);
                // In an active term, higher plans are upgrades (prorated now);
                // the current and lower plans are changed from the next term.
                const isNextTermOption = activeUpgradeFlow && Boolean(currentPlan) && itemCredits <= currentCredits;
                const isCurrentPlan = activeUpgradeFlow && currentPlan?.planId === item.planId;
                const isUpgradeOption = activeUpgradeFlow && !isNextTermOption;
                const unavailable = !licenseLoaded || licenseLoadError || (isUpgradeOption && (
                  !currentPlan || !["monthly", "annual"].includes(license?.billingCycle)
                ));
                return (
              <button
                key={item.planId}
                type="button"
                className={"payment-plan-card" + (item.recommended ? " is-recommended" : "") + (unavailable ? " is-unavailable" : "")}
                disabled={unavailable}
                onClick={() => {
                  const url = new URLSearchParams({ planId: item.planId });
                  if (isNextTermOption) {
                    // Billing cycle is chosen on the next step.
                  } else if (isUpgradeOption) {
                    url.set("purchase", "upgrade");
                    url.set("cycle", license.billingCycle);
                  } else if (boxAddonSelected) {
                    url.set("boxAddon", "true");
                  }
                  routerHistory.push(
                    "/settings/invoice?" + url.toString()
                  );
                }}
              >
                {item.recommended && (
                  <span className="payment-plan-badge">
                    {text('おすすめ', 'Recommended')}
                  </span>
                )}
                <span className="payment-plan-name">{isEnglish ? item.labelEn : item.label}</span>
                <span className="payment-plan-tagline">{isEnglish ? item.taglineEn : item.tagline}</span>
                <span className="payment-plan-price">
                  <small>{"\u00a5"}</small>
                  {Number(item.annual).toLocaleString()}
                  <em>{text('/月', '/month')}</em>
                </span>
                <span className="payment-plan-annual-note">
                  {text('年次請求時の月額換算', 'Monthly equivalent with annual billing')}
                </span>
                <span className="payment-plan-meta">
                  <span>
                    {text('月次請求: ¥', 'Monthly billing: ¥')}{Number(item.monthly).toLocaleString()}
                  </span>
                  <span>
                    {text('付与クレジット: ', 'Granted credits: ')}{Number(item.credits).toLocaleString()}
                  </span>
                </span>
                {item.upcomingChange && (
                  <span className="payment-plan-annual-note" style={{ color: "#b45309" }}>
                    {text(
                      `${formatPlanChangeDate(item.upcomingChange.effectiveFrom, false)}から 月額¥${Number(item.upcomingChange.monthlyPriceAnnual).toLocaleString()}（年次）／月次¥${Number(item.upcomingChange.monthlyPriceMonthly).toLocaleString()}・付与${Number(item.upcomingChange.grantedCredits).toLocaleString()}クレジット`,
                      `From ${formatPlanChangeDate(item.upcomingChange.effectiveFrom, true)}: ¥${Number(item.upcomingChange.monthlyPriceAnnual).toLocaleString()}/month (annual), ¥${Number(item.upcomingChange.monthlyPriceMonthly).toLocaleString()} monthly, ${Number(item.upcomingChange.grantedCredits).toLocaleString()} credits`
                    )}
                  </span>
                )}
                <span className="payment-plan-action">
                  {isCurrentPlan
                    ? text('ご契約中（請求サイクル変更・予約の取消）', 'Current plan (change cycle or cancel a scheduled change)')
                    : isNextTermOption
                    ? text('次期契約から変更', 'Change from next term')
                    : unavailable && isUpgradeOption
                    ? text('次期契約で選択可能', 'Available next term')
                    : isUpgradeOption
                      ? text('アップグレードを見積もる', 'Review upgrade quote')
                      : text('このプランを選択', 'Select this plan')}
                  <span aria-hidden="true">{" \u2192"}</span>
                </span>
              </button>
                );
              })()
            ))}
          </div>
        </section>
      )}
      {planId && !selectedCycle && !invoice && (
        <div
          style={{
            marginTop: 16,
            padding: 18,
            border: "1px solid #dbe4ff",
            background: "#f8fbff",
            borderRadius: 12,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
            {text('請求サイクルを選択してください', 'Select a billing cycle')}
          </div>

          {boxAddonSelected && (
            <div className="payment-combined-purchase-note">
              <strong>{text('BOX連携オプションを含む', 'Includes Box integration')}</strong>
              <span>{text('選択したプランとBOX連携料金を1枚の請求書にまとめます。', 'The selected plan and Box integration fee will be combined into one invoice.')}</span>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setSelectedCycle("annual")}
              style={{
                minWidth: 180,
                height: 48,
                borderRadius: 10,
                border:
                  selectedCycle === "annual"
                    ? "2px solid #6163FF"
                    : "1px solid #cbd5e1",
                background: selectedCycle === "annual" ? "#eef2ff" : "#fff",
                color: "#0f172a",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              {text('年次請求', 'Annual billing')}
            </button>

            <button
              type="button"
              onClick={() => setSelectedCycle("monthly")}
              style={{
                minWidth: 180,
                height: 48,
                borderRadius: 10,
                border:
                  selectedCycle === "monthly"
                    ? "2px solid #6163FF"
                    : "1px solid #cbd5e1",
                background: selectedCycle === "monthly" ? "#eef2ff" : "#fff",
                color: "#0f172a",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              {text('月次請求', 'Monthly billing')}
            </button>
          </div>
        </div>
      )}

      {upgradePurchase && !invoice && (
        <section className="payment-plan-selector" aria-live="polite" style={{ marginTop: 16 }}>
          <div className="payment-step-heading">
            <span className="payment-step-number">2</span>
            <div>
              <h3>{text('アップグレード内容の確認', 'Review upgrade')}</h3>
              <p>{text('差額のみを残りの契約期間に応じて計算します。更新日は変わりません。', 'Only the prorated price difference is charged. Your renewal date stays the same.')}</p>
            </div>
          </div>
          {upgradeQuoteLoading && <p>{text('見積もりを計算しています…', 'Calculating quote…')}</p>}
          {upgradeQuoteError && <p role="alert" style={{ color: '#b42318' }}>{upgradeQuoteError}</p>}
          {upgradeQuote && (
            <div style={{ padding: 18, border: '1px solid #dbe4ff', borderRadius: 12, background: '#f8fbff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <strong>{upgradeQuote.planName || PLANS.find((item) => item.planId === planId)?.[isEnglish ? 'labelEn' : 'label']}</strong>
                <strong>{text('税込 ¥', 'Total ¥')}{Math.round(upgradeQuote.proratedAmount * 1.1).toLocaleString()}</strong>
              </div>
              <div style={{ marginTop: 8, color: '#4a5568' }}>
                {text('税抜の差額: ¥', 'Prorated difference before tax: ¥')}{Number(upgradeQuote.proratedAmount).toLocaleString()} · {text('追加クレジット: ', 'Additional credits: ')}{Number(upgradeQuote.additionalCredits || 0).toLocaleString()}
              </div>
              {upgradeQuote.periodEndDate && (
                <div style={{ marginTop: 6, color: '#4a5568' }}>
                  {text('次回更新日: ', 'Next renewal: ')}{new Date(upgradeQuote.periodEndDate).toLocaleDateString(isEnglish ? 'en-US' : 'ja-JP', { timeZone: 'Asia/Tokyo' })}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {hasPurchaseSelection && billingProfile.complete !== null && (
        <div className="billing-profile-summary" style={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12,
          margin: "0 0 16px", padding: "12px 16px", borderRadius: 8,
          border: billingProfile.complete ? "1px solid #e2e8f0" : "1px solid #fcd34d",
          background: billingProfile.complete ? "#f8fafc" : "#fffbeb", fontSize: 15,
        }}>
          <span>
            {billingProfile.complete
              ? <>{text('請求先：', 'Bill to: ')}<strong>{billingProfile.name}</strong>{text(' 様', '')}</>
              : text('請求書の宛名・住所（請求先情報）が未入力です。', 'Your billing name and address are not entered yet.')}
          </span>
          <button
            type="button"
            onClick={() => setProfileModal({ action: null })}
            style={{ background: "none", border: "none", color: "#1f63d6", textDecoration: "underline", cursor: "pointer", fontSize: 15, padding: 0 }}
          >
            {billingProfile.complete ? text('変更', 'Change') : text('入力する', 'Enter now')}
          </button>
        </div>
      )}

      {profileModal && (
        <BillingProfileModal
          onSaved={handleBillingProfileSaved}
          onClose={() => setProfileModal(null)}
          submitLabel={profileModal.action ? text('保存して請求書を発行', 'Save and issue the invoice') : undefined}
        />
      )}

      <div className={"payment-terms-step" + (termsEnabled ? " is-enabled" : " is-disabled")}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            cursor: termsEnabled ? "pointer" : "not-allowed",
            userSelect: "none",
            gap: 10,
          }}
        >
          <input
            type="checkbox"
            disabled={!termsEnabled}
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{ width: 20, height: 20 }}
          />
          <span style={{ fontSize: 16 }}>
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "underline",
                color: "#6163FF",
                fontWeight: 700,
              }}
            >
              {text('利用規約', 'Terms of service')}
            </a>
            {text('に同意します', 'I agree')}
          </span>
        </label>
      </div>

      <div className="invoice-actions" style={{ display: "flex", gap: 20, marginTop: 24, flexWrap: "wrap" }}>
        <button
          onClick={handleWebInvoice}
          className="button button-primary"
          disabled={disabled}
          style={{
            minWidth: 290,
            height: 62,
            fontSize: 18,
            fontWeight: 800,
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {nextTermChange
            ? text('次期契約の変更を予約', 'Schedule for next term')
            : text('Webで請求書を表示', 'View invoice online')}
        </button>

        {!nextTermChange && (
        <button
          onClick={handleEmailRequest}
          className="button button-dark"
          disabled={disabled}
          style={{
            minWidth: 220,
            height: 62,
            fontSize: 18,
            fontWeight: 800,
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {text('メールで送信', 'Send by email')}
        </button>
        )}
      </div>

      {apiError && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            border: "1px solid #e74c3c",
            color: "#e74c3c",
            borderRadius: 8,
            background: "#fff5f5",
          }}
        >
          ❌ {apiError}
        </div>
      )}
      {successMessage && (
        <div className="invoice-settings-success" role="status" aria-live="polite">
          {successMessage}
        </div>
      )}

      {readOnlyInvoice && <p role="status">{text('この請求書は閲覧専用です。お支払い・メール送信はできません。', 'This invoice is read-only. Payment and email actions are disabled.')}</p>}

      {copyMessage && (
        <div
          style={{
            marginTop: 16,
            padding: "10px 14px",
            background: "#eef4ff",
            border: "1px solid #cfe0ff",
            color: "#2657d8",
            borderRadius: 10,
            display: "inline-block",
            fontSize: 14,
            fontWeight: 800,
            boxShadow: "0 4px 12px rgba(38, 87, 216, 0.10)",
          }}
        >
          {copyMessage}
        </div>
      )}

      {invoice && invoice.status !== "PAID" && (
        <div
          className="invoice-settings-generated-card"
          style={{
            marginTop: 28,
            background: "#fff",
            border: "1px solid #e6ebf2",
            borderRadius: 18,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
            overflow: "hidden",
          }}
        >
          <div className="invoice-settings-generated-card-inner" style={{ padding: "40px 42px 28px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 24,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 46,
                    color: "#2657d8",
                    letterSpacing: "0.03em",
                    fontWeight: 800,
                  }}
                >
                  {text('御請求書', 'Invoice')}
                </h3>
              </div>

              <div
                style={{
                  minWidth: 300,
                  color: "#1f2a3d",
                  lineHeight: 1.8,
                  fontWeight: 700,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span>{text('登録番号:', 'Registration no.:')}</span>
                  <span>{registrationNumber}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span>{text('請求書番号:', 'Invoice no.:')}</span>
                  <span>{invoiceNumber}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span>{text('発行日:', 'Issue date:')}</span>
                  <span>{formatDate(invoice.createdAt)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span>{text('支払期限:', 'Due date:')}</span>
                  <span>{formatDate(invoice.dueDate)}</span>
                </div>
              </div>
            </div>

            <hr
              style={{
                margin: "28px 0 26px",
                border: "none",
                borderTop: "1px solid #e8edf4",
              }}
            />

            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#1b2437",
                marginBottom: 28,
              }}
            >
              {customerCompanyName} {text('様', '')}
            </div>

            <div
              style={{
                background: "#f7f8fa",
                borderRadius: 10,
                padding: "28px 20px",
                textAlign: "center",
                marginBottom: 34,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  color: "#606e82",
                  marginBottom: 8,
                  fontWeight: 700,
                }}
              >
                {text('ご請求金額（税込）', 'Invoice total (including tax)')}
              </div>

              <div
                style={{
                  fontSize: 54,
                  fontWeight: 900,
                  color: "#1c2538",
                  letterSpacing: "0.03em",
                }}
              >
                {amountText}
              </div>

              {isAnnual && Number.isFinite(planMonthlyAmountForDisplay) && (
                <div style={{ marginTop: 14, fontWeight: 800, color: "#334155" }}>
                  {text('プラン料金', 'Plan fee')}: ¥{planMonthlyAmountForDisplay.toLocaleString()} × 12 {text('か月', 'months')} = ¥{planBaseAmountForDisplay.toLocaleString()} ({text('税別', 'before tax')})
                </div>
              )}

              {invoice?.boxAddonIncluded && (
                <div style={{ marginTop: 12, fontWeight: 800, color: "#334155" }}>
                  {"BOX\u9023\u643a\u30aa\u30d7\u30b7\u30e7\u30f3: "}
                  {"\u00a5"}{Number(invoice.boxAddonUnitPrice || 500).toLocaleString()}{" \u00d7 "}{invoice.boxAddonQuantity || 1}
                  {isAnnual ? " \u00d7 12\u304b\u6708" : ""}
                  {" = \u00a5"}{Number(invoice.boxAddonAmount || 0).toLocaleString()} {"(\u7a0e\u5225)"}
                </div>
              )}

              {Number.isFinite(baseAmountForDisplay) &&
                Number.isFinite(taxAmountForDisplay) &&
                Number.isFinite(totalAmount) && (
                  <div
                    style={{
                      marginTop: 12,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 8,
                      fontSize: 20,
                      fontWeight: 800,
                      lineHeight: 1.6,
                    }}
                  >
                    <span style={{ color: "#606e82" }}>
                      ¥{baseAmountForDisplay.toLocaleString()}
                    </span>
                    <span style={{ color: "#606e82" }}>+</span>
                    <span
                      style={{
                        color: "#dc2626",
                        background: "#fef2f2",
                        padding: "2px 10px",
                        borderRadius: 999,
                        border: "1px solid #fecaca",
                      }}
                    >
                      {text('10% 税', '10% tax')} ¥{taxAmountForDisplay.toLocaleString()}
                    </span>
                    <span style={{ color: "#606e82" }}>=</span>
                    <span style={{ color: "#1c2538" }}>
                      ¥{totalAmount.toLocaleString()}
                    </span>
                  </div>
                )}
            </div>

            <div
              className="invoice-settings-bank-panel"
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: "22px 28px 18px",
                background: "#fff",
              }}
            >
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 900,
                  color: "#111827",
                  marginBottom: 14,
                }}
              >
                {text('お振込先情報', 'Bank transfer details')}
              </div>

              <div style={{ borderTop: "1px solid #edf1f7" }}>
                {detailRow("金融機関", bank?.name || "-", "金融機関")}
                {detailRow("支店コード", bank?.branchCode || "-", "支店コード")}
                {detailRow("支店名", bank?.branchName || "-", "支店名")}
                {detailRow("口座種別", bank?.type || "-", "口座種別")}
                {detailRow("口座番号", bank?.number || "-", "口座番号")}
                {detailRow("口座名義", "PITHIAS Technologies合同会社", "口座名義")}
                {detailRow("口座名義（カナ）", bank?.holder || "-", "口座名義（カナ）")}
              </div>
            </div>

            <div
              style={{
                marginTop: 26,
                textAlign: "center",
                color: "#7b8595",
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              {text('※振込手数料は貴社負担にてお願いいたします。', 'Bank transfer fees are the customer’s responsibility.')}
              <div style={{ fontSize: 13, fontWeight: 400, marginTop: 6 }}>
                {text('※支払期限当日に銀行の営業時間外にお振込みいただいた場合、翌営業日の入金扱いとなり、期限を過ぎることがあります。', 'A transfer made after bank hours on the due date is booked on the next bank business day and may count as late.')}
              </div>
            </div>

            <div
              style={{
                marginTop: 28,
                padding: 18,
                border: "1px solid #edf1f7",
                borderRadius: 12,
                background: "#fbfcfe",
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  marginBottom: 10,
                  color: "#172033",
                }}
              >
                {text('お支払い状況', 'Payment status')}
              </div>

              <div style={{ fontSize: 15, lineHeight: 1.9, color: "#334155" }}>
                <div>請求ID: {invoice.id || invoice.invoiceId || "-"}</div>
                <div>
                  プラン: {plan?.planName || plan?.planId || invoice.planId || planId} /{" "}
                  {cycleLabel}
                </div>
                {isAnnual && annualBreakdownText ? (
                  <>
                    <div>年額内訳: {annualBreakdownText}</div>
                    {Number.isFinite(taxAmountForDisplay) && Number.isFinite(totalAmount) && (
                      <div style={{ color: "#dc2626", fontWeight: 800 }}>
                        税込内訳: ¥{annualBaseAmountForDisplay.toLocaleString()} + 消費税10% ¥{taxAmountForDisplay.toLocaleString()} = ¥{totalAmount.toLocaleString()}
                      </div>
                    )}
                  </>
                ) : null}
                <div>ステータス: {settlementLabel(invoice)}</div>
                <div>期限: {formatDateTime(invoice.dueDate)}</div>
                {invoice.paidAmount != null ? (
                  <div>入金額: ¥{Number(invoice.paidAmount).toLocaleString()}</div>
                ) : null}
                {invoice.grantedCredits != null ? (
                  <div>
                    付与クレジット:{" "}
                    {invoice.grantedCredits > 0
                      ? `+${invoice.grantedCredits}`
                      : "0"}
                  </div>
                ) : null}
              </div>
            </div>

            <div
              style={{
                marginTop: 18,
                fontSize: 14,
                color: "#5b677a",
                lineHeight: 1.8,
              }}
            >
              {isUsableUnpaidInvoice(invoice) && text('上記口座へ銀行振込してください。入金確認後、自動でプランが有効化されます。', 'Transfer the payment to the account above. Your plan will activate automatically after confirmation.')}
            </div>

            {isUsableUnpaidInvoice(invoice) && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 14,
                  color: "#7a8698",
                  lineHeight: 1.8,
                }}
              >
                {text('※ 入金確認は数秒〜数分かかる場合があります。ページを閉じても問題ありません。', 'Payment confirmation may take a few seconds to several minutes. You may close this page.')}
              </div>
            )}
          </div>
        </div>
      )}

      {instruction && isUsableUnpaidInvoice(invoice) && (
        <div style={{ marginTop: 14, fontSize: 13, color: "#4b5563" }}>
          {instruction}
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: 13, color: "#6b7280" }}>
        {nextTermChange
          ? text('※ 現在の契約期間中は変更されず、追加の請求もありません。次回更新日から新しいプラン・請求サイクルが適用されます。', 'Nothing changes and nothing is charged during the current term. The new plan and billing cycle start at your next renewal.')
          : text('※「Webで請求書を表示」をクリックすると、振込先情報の確認が可能です。', 'Select “View invoice online” to review the bank transfer information.')}
      </div>

      <hr
        style={{
          margin: "40px 0",
          border: "none",
          borderTop: "1px solid #ddd",
        }}
      />

      <h3 style={{ fontSize: 36, color: "#111827", marginBottom: 14 }}>
        {text('請求・お支払い履歴', 'Billing and payment history')}
      </h3>

      <button type="button" className="button button-sm" disabled={!!receiptLoading} onClick={async () => {
        try {
          if (invoice?.status === 'UNPAID') await api.post(API_ENDPOINTS.BILLING_RECONCILE, {});
          if (invoice?.id) await refreshCurrentInvoice(invoice.id, invoice); else await loadHistory();
        }
        catch (_) { setApiError(text('入金状況を更新できませんでした。再度お試しください。', 'Unable to refresh payment status. Please try again.')); }
      }}>
        {text('入金状況を更新', 'Refresh payment status')}
      </button>
      {invoice?.status === 'UNPAID' && <p style={{fontSize: 14}}>{text('入金状況は30秒ごとに最大5分間確認します。それ以降は「入金状況を更新」を押してください。', 'Payment status is checked every 30 seconds for up to 5 minutes. After that, select “Refresh payment status”.')}</p>}
      <div className="invoice-history-scroll" style={{ overflowX: "auto", marginTop: 16 }}>
        {receiptError && <p role="alert" style={{ color: '#c62828' }}>{receiptError}</p>}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 14,
            textAlign: "left",
            background: "#fff",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <thead>
            <tr style={{ background: "#f5f7fb", borderBottom: "2px solid #e5e7eb" }}>
              <th style={{ padding: 14, whiteSpace: "nowrap" }}>{text('日付', 'Date')}</th>
              <th style={{ padding: 14 }}>{text('内容', 'Description')}</th>
              <th style={{ padding: 14, textAlign: "right" }}>{text('金額', 'Amount')}</th>
              <th style={{ padding: 14, textAlign: "center" }}>{text('クレジット', 'Credits')}</th>
              <th style={{ padding: 14, textAlign: "center" }}>{text('ステータス', 'Status')}</th>
              <th style={{ padding: 14 }}>{text('操作', 'Action')}</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 14, color: "#666" }}>
                  {text('履歴がありません', 'No billing history')}
                </td>
              </tr>
            ) : (
              history.map((row) => (
                <tr
                  key={row.id || row.invoiceId}
                  className={['EXPIRED','CANCELED'].includes(invoiceDisplayStatus(row)) ? 'invoice-history-inactive' : ''}
                  style={{ borderBottom: "1px solid #eef2f7" }}
                >
                  <td style={{ padding: 14 }}>
                    {(row.createdAt || row.date || "").slice(0, 10) || "-"}
                  </td>
                  <td style={{ padding: 14 }}>
                    <div>{row.kind === 'BOX_ADDON' ? text('BOX連携オプション', 'BOX add-on') :
                      (PLANS.find(p => p.planId === row.planId)?.[isEnglish ? 'labelEn' : 'label'] || row.description || row.planId || '-')}
                      {row.kind !== 'BOX_ADDON' && row.boxAddonIncluded && (
                        <span className="invoice-history-addon"> + {text('BOX連携', 'BOX add-on')}</span>
                      )}
                    </div>
                    <div className="invoice-history-reference">INV-{String(row.id || row.invoiceId).slice(0,8).toUpperCase()}</div>
                  </td>
                  <td style={{ padding: 14, textAlign: "right" }}>
                    {row.amount ? `¥${Number(row.amount).toLocaleString()}` : "-"}
                  </td>
                  <td style={{ padding: 14, textAlign: "center" }}>
                    {row.grantedCredits != null
                      ? row.grantedCredits > 0
                        ? `+${row.grantedCredits}`
                        : "0"
                      : "-"}
                  </td>
                  <td style={{ padding: 14, textAlign: "center" }}>
                    <span
                      style={{
                        background: ['EXPIRED','CANCELED'].includes(invoiceDisplayStatus(row)) ? '#6b7280' : row.status === "PAID" ? "#39b76a" : "#f39c12",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {settlementLabel(row)}
                    </span>
                  </td>
                  <td style={{ padding: 14, whiteSpace: 'nowrap' }}>
                    {row.status === 'PAID' && row.paidAt && Number(row.paidAmount) >= Number(row.amount) ? (
                      <button type="button" className="invoice-history-link" disabled={receiptLoading !== null}
                        onClick={() => downloadReceipt(row)}>
                        {receiptLoading === (row.id || row.invoiceId) ? text('ダウンロード中…', 'Downloading…') : text('領収書をダウンロード', 'Download receipt')}
                      </button>
                    ) : isUsableUnpaidInvoice(row) ? <button type="button" className="invoice-history-link"
                      onClick={() => {
                        const invoiceId = row.id || row.invoiceId;
                        setPaymentSessionValue(ACTIVE_PAYMENT_INVOICE_KEY, invoiceId);
                        setAgreed(false);
                        setApiError(null);
                        routerHistory.push(`/settings/invoice?invoiceId=${invoiceId}`);
                      }}>
                      {text('請求書を表示', 'View invoice')}
                    </button> : <span aria-label={text('操作なし', 'No action available')}>—</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
